import { DocumentModal } from "@/shared/ui/DocumentViewerModal.jsx";
import { DocumentEditorModal } from "@/shared/ui/DocumentEditorModal.jsx";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Share2, Lock, Globe, Trash2, Building, Eye, FileText, PenTool, Users, History, Loader2, Edit, Copy, Clock, MessageSquare, CheckCircle, Archive, ArchiveRestore, Info } from "lucide-react";
import { PropertiesModal } from "@/shared/ui/PropertiesModal.jsx";
import { FileTypeIcon } from '@/shared/ui/FileTypeIcon.jsx';
import { isCollaboraEditable } from '@/shared/utils/fileKind.js';
import { atLeast, canEditFile } from '@/shared/utils/nodeAccess.js';
import { SharingModal } from '@/modules/shared/components/SharingModal.jsx';
import { DeleteFile } from '@/modules/my-docs/features/DeleteFile.jsx';
import { DocumentTypeSelector } from '@/modules/signing/components';
import { VersionHistoryModal } from '@/modules/files/features/VersionHistoryModal.jsx';
import { ActivityPanel } from '@/modules/my-docs/components/ActivityPanel.jsx';
import { CommentsPanel } from '@/modules/my-docs/components/CommentsPanel.jsx';
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { usePatchRequest, usePostRequest, useGetRequest } from "@/shared/hooks/requests";
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';
import { ArchiveConfirmDialog, RestoreConfirmDialog } from '@/shared/ui/ArchiveRestoreDialogs.jsx';
import { FILE_CLONE, FILE_ARCHIVE, FILE_RESTORE, FILE_RESTORE_TARGET } from "@/shared/utils/urls";
import { cn } from "@/lib/utils";

import { useTranslation } from 'react-i18next';


// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const DocumentItem = ({
  doc,
  share,
  owner,
  onDelete,
  isEimzoDocument = false,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
  // 'card' (default) renders the My Docs tile; 'row' renders a list-view
  // line — used by search results so they follow the list/grid toggle.
  variant = 'card',
}) => {
  const { t } = useTranslation(['common', 'my-docs', 'signing', 'shared', 'mainPage']);
  const [openDoc, setOpenDoc] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [newVisibility, setNewVisibility] = useState(doc.visibility || 'private');
  const [approvalStateOpen, setApprovalStateOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneFilename, setCloneFilename] = useState('');
  const [activityOpen, setActivityOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreNeedsPick, setRestoreNeedsPick] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const updateVisibility = usePatchRequest({ url: `embed/v1/file/${doc.id}/visibility` });
  const cloneFile = usePostRequest({ url: FILE_CLONE.replace('{id}', doc.id) });
  const archiveFile = usePostRequest({ url: FILE_ARCHIVE.replace('{file_id}', doc.id) });
  const restoreFile = usePostRequest({ url: FILE_RESTORE.replace('{file_id}', doc.id) });
  const checkRestoreTarget = useGetRequest({ url: FILE_RESTORE_TARGET.replace('{file_id}', doc.id) });

  // 🗂️ Normalize extension (handle both camelCase and snake_case)
  const extension = doc.extension;
  const approvalState = doc.approvalState || doc.approval_state;
  const rawExt = (extension || "file").toLowerCase();

  // Check if file is editable (by extension)
  const isEditable = isCollaboraEditable(doc);

  const isSigned = approvalState === 'signed';
  // Who may edit is the backend's one rule on the read (`edit_block` /
  // `can_edit`): nobody while the file is locked, being signed, under
  // agreement or filed.
  const canEdit = isEditable && canEditFile(doc);
  // Editor level or above (a share at editor), for the "Shared with me" label.
  const hasWriteAccess = atLeast(doc, 'editor');

  // Owner-only actions: share, change visibility, delete, upload-version, etc.
  const canShare = Boolean(share && owner);
  // Whether to render the right-click menu at all — sharees with edit access
  // get a slimmer menu (Edit + read-only items only).
  const canOpenMenu = canShare || canEdit;

  const handleContextMenu = (e) => {
    if (!canOpenMenu) return;
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setMenuOpen(true);
  };

  const handleVisibilityChange = async () => {
    try {
      const { success } = await updateVisibility.request({
        data: { visibility: newVisibility },
      });
      if (success) {
        toast.success(t('my-docs:visibility.updated'));
        setVisibilityOpen(false);
        if (onDelete) onDelete(); // Refresh the list
      }
    } catch (err) {
      console.error(err);
      toast.error(t('my-docs:visibility.error'));
    }
  };

  const handleCloneFile = async () => {
    try {
      const { success } = await cloneFile.request({
        data: {},
      });
      if (success) {
        toast.success(t('my-docs:clone.success') || 'File cloned successfully');
        setCloneOpen(false);
        setCloneFilename('');
        if (onDelete) onDelete(); // Refresh the list
      } else {
        toast.error(t('my-docs:clone.error') || 'Failed to clone file');
      }
    } catch (err) {
      console.error(err);
      toast.error(t('my-docs:clone.error') || 'Failed to clone file');
    }
  };

  const handleEdit = () => {
    setEditOpen(true);
  };

  const handleOpenClone = () => {
    setCloneFilename(`${doc.name}_copy`);
    setCloneOpen(true);
  };

  const handleConfirmArchive = async () => {
    const { success } = await archiveFile.request({ data: {} });
    if (success) {
      toast.success(t('my-docs:archive.success') || 'Document archived');
      setArchiveOpen(false);
      if (onDelete) onDelete();
    } else {
      toast.error(t('my-docs:archive.error') || 'Failed to archive document');
    }
  };

  const handleOpenRestore = async () => {
    try {
      const { response } = await checkRestoreTarget.request();
      setRestoreNeedsPick(Boolean(response?.needsFolderPick));
      setRestoreOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(t('my-docs:restore.error'));
    }
  };

  const handleConfirmRestore = async () => {
    const { success } = await restoreFile.request({
      data: restoreNeedsPick ? { new_folder_id: null } : {},
    });
    if (success) {
      toast.success(t('my-docs:restore.success'));
      setRestoreOpen(false);
      if (onDelete) onDelete();
    } else {
      toast.error(t('my-docs:restore.error'));
    }
  };

  const isArchived = Boolean(doc.archivedAt);

  const handleOpenClick = (e) => {
    // Selection mode short-circuits open: if anything is selected,
    // clicking the card toggles selection instead of opening the file.
    if (isSelectable && isSelected) {
      e.stopPropagation();
      onToggleSelect?.();
      return;
    }
    setOpenDoc(true);
  };

  return (
    <>
      {variant === 'row' ? (
        /* List-view row — mirrors the FoldersTable list layout */
        <div
          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors cursor-pointer group"
          onClick={handleOpenClick}
          onContextMenu={handleContextMenu}
        >
          <div className="w-10 h-10 rounded-[4px] flex items-center justify-center shrink-0">
            <div className="w-7">
              <FileTypeIcon file={doc} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <TruncatedTooltip content={doc.name}>
              <span className="text-sm font-medium text-foreground truncate cursor-default">
                {doc.name}
              </span>
            </TruncatedTooltip>
          </div>
          <div className="hidden sm:block w-24 text-right text-xs text-muted-foreground shrink-0">
            {(doc.name)?.split('.').pop()?.toUpperCase() || 'FILE'} <span className="mx-1">|</span> {formatFileSize(doc.size || doc.size_bytes)}
          </div>
          <div className="hidden sm:block w-32 text-right text-xs text-muted-foreground shrink-0">
            {formatDate(doc.createdAt || doc.created_at)}
          </div>
        </div>
      ) : (
      <div
        className="flex flex-col w-[135px] group cursor-pointer"
        onClick={handleOpenClick}
        onContextMenu={handleContextMenu}
      >
        {/* Tile */}
        <div className={
          // No tinted tile behind the icon — matches the My Docs grid, where
          // file and folder cards sit flat on the page.
          `w-[135px] h-[135px] rounded-[4px] p-4 relative flex flex-col transition-transform group-hover:scale-105 ` +
          (isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : '')
        }>
          {/* Selection checkbox — shown on hover or when selected */}
          {isSelectable && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
              className={
                `absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ` +
                (isSelected
                  ? 'bg-primary border-primary text-white opacity-100'
                  : 'bg-white/80 border-muted-foreground/40 text-transparent opacity-0 max-md:opacity-100 group-hover:opacity-100 hover:border-primary')
              }
              aria-label={isSelected ? t('my-docs:bulk.deselect') : t('my-docs:bulk.select')}
            >
              <svg viewBox="0 0 16 16" className="w-3 h-3" fill="currentColor">
                <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
              </svg>
            </button>
          )}
          {/* Centered branded file-type icon — w-20 matches the My Docs grid
              (96px tall at the sheet's 1:1.2 aspect, the folder icon's height). */}
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="w-20">
              <FileTypeIcon file={doc} />
            </div>
          </div>

          {/* Badges float over the tile's bottom-left corner so the
              full-height icon never gets squeezed by them. */}
          <div className="absolute bottom-2 left-2 z-10 flex flex-col gap-1 items-start">
            {/* E-IMZO Badge */}
            {approvalState === 'signed' && isEimzoDocument && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100/80 text-purple-700 border-0 backdrop-blur-sm">
                <CheckCircle className="w-3 h-3" />
                <span>E-IMZO</span>
              </span>
            )}

            {/* Visibility Badge — for non-owners with edit access (i.e. the
                file was shared with them via FileShare) we show "Shared with me"
                instead of the file's intrinsic visibility, which from their
                perspective is the more meaningful label. */}
            {!owner && hasWriteAccess ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white/90 shadow-sm text-purple-600">
                <Users className="w-3 h-3 flex-shrink-0" />
                <span className="truncate max-w-[80px]">
                  {t('my-docs:visibility.shared') || 'Shared with me'}
                </span>
              </span>
            ) : (
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white/90 shadow-sm",
                doc.visibility === 'private' ? "text-gray-600" :
                  doc.visibility === 'project' ? "text-blue-600" : // organization/project style
                    "text-green-600"
              )}>
                {doc.visibility === "private" && <Lock className="w-3 h-3 flex-shrink-0" />}
                {doc.visibility === "project" && <Building className="w-3 h-3 flex-shrink-0" />}
                {doc.visibility === "public" && <Globe className="w-3 h-3 flex-shrink-0" />}

                <span className="truncate max-w-[80px]">
                  {doc.visibility === "private" && t('my-docs:visibility.private')}
                  {doc.visibility === "project" && t('my-docs:visibility.organization')}
                  {doc.visibility === "public" && t('my-docs:visibility.public')}
                  {!doc.visibility && t('my-docs:visibility.private')}
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 w-full text-left bg-transparent">
          <TruncatedTooltip content={doc.name}>
            <h3 className="font-medium text-sm text-foreground mb-1 line-clamp-2 break-words leading-tight">
              {doc.name}
            </h3>
          </TruncatedTooltip>
          <p className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
            {(doc.name)?.split('.').pop()?.toUpperCase() || 'FILE'} <span className="mx-1">|</span> {formatFileSize(doc.size || doc.size_bytes)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('mainPage:edited', { date: formatDate(doc.createdAt || doc.created_at) })}
          </p>
        </div>
      </div>
      )}

      {/* Document Viewer Modal */}
      <DocumentModal open={openDoc} onClose={() => setOpenDoc(false)} doc={doc} />

      {/* Right-click Context Menu */}
      {
        canOpenMenu && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              {/* hidden trigger */}
              <button className="hidden" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              style={{ position: "fixed", top: menuPosition.y, left: menuPosition.x }}
            >
              {/* Owner-only: share, change visibility, copy public link */}
              {canShare && (
                <>
                  <DropdownMenuItem onClick={() => setOpenShare(true)} className="cursor-pointer">
                    <Share2 className="mr-2 h-4 w-4" />
                    {t('my-docs:contextMenu.share')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    setNewVisibility(doc.visibility || 'private');
                    setVisibilityOpen(true);
                  }} className="cursor-pointer">
                    <Eye className="mr-2 h-4 w-4" />
                    {t('my-docs:contextMenu.changeVisibility')}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                </>
              )}

              {/* Edit & Clone */}
              {canEdit && (
                <DropdownMenuItem onClick={handleEdit} className="cursor-pointer">
                  <Edit className="mr-2 h-4 w-4" />
                  {t('my-docs:contextMenu.edit') || 'Edit'}
                </DropdownMenuItem>
              )}
              {/* Show Clone for signed documents or as alternative to edit (owner only) */}
              {owner && (isSigned || (isEditable && !canEdit)) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={handleOpenClone} className="cursor-pointer">
                        <Copy className="mr-2 h-4 w-4" />
                        {t('my-docs:contextMenu.clone') || 'Clone'}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('my-docs:contextMenu.cloneHint') || 'Create an editable copy of this document'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {/* Clone option for any file (owner only) */}
              {owner && !isSigned && (
                <DropdownMenuItem onClick={handleOpenClone} className="cursor-pointer">
                  <Copy className="mr-2 h-4 w-4" />
                  {t('my-docs:contextMenu.clone') || 'Clone'}
                </DropdownMenuItem>
              )}

              {/* Owner-only: versioning */}
              {/* No manual "upload new version": every editor save is
                  versioned automatically — open the file and edit. */}
              {canShare && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => setVersionHistoryOpen(true)} className="cursor-pointer">
                <History className="mr-2 h-4 w-4" />
                {t('my-docs:contextMenu.versionHistory') || 'Version History'}
              </DropdownMenuItem>

              {/* Owner-only: document type & signing setup */}
              {canShare && isEimzoDocument && (
                <>
                  <DropdownMenuSeparator />
                  {(() => {
                    // Signed is terminal — clone to start over. Agreement
                    // can still escalate to signed (cascades participants).
                    const typeLocked = approvalState === 'signed';
                    return (
                      <DropdownMenuItem
                        onClick={() => { if (!typeLocked) setApprovalStateOpen(true); }}
                        disabled={typeLocked}
                        className={typeLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                        title={typeLocked ? (t('my-docs:docType.lockedHint') || 'Signed documents are terminal. Clone the file to start over.') : undefined}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {t('my-docs:contextMenu.changeDocumentType')}
                      </DropdownMenuItem>
                    );
                  })()}
                </>
              )}

              <DropdownMenuSeparator />

              {/* Activity */}
              <DropdownMenuItem onClick={() => setActivityOpen(true)} className="cursor-pointer">
                <Clock className="mr-2 h-4 w-4" />
                {t('my-docs:contextMenu.activity') || 'Activity'}
              </DropdownMenuItem>

              {/* Comments */}
              <DropdownMenuItem onClick={() => setCommentsOpen(true)} className="cursor-pointer">
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('my-docs:contextMenu.comments') || 'Comments'}
              </DropdownMenuItem>

              {/* Properties */}
              <DropdownMenuItem onClick={() => setPropsOpen(true)} className="cursor-pointer">
                <Info className="mr-2 h-4 w-4" />
                {t('my-docs:properties.title') || 'Properties'}
              </DropdownMenuItem>

              {/* Owner-only: archive/restore/delete. Signed docs cannot be
                  deleted — Archive replaces Delete for them. Archived docs
                  show Restore (and nothing else from the delete row). */}
              {canShare && (
                <>
                  <DropdownMenuSeparator />
                  {isArchived ? (
                    <DropdownMenuItem onClick={handleOpenRestore} className="cursor-pointer">
                      <ArchiveRestore className="mr-2 h-4 w-4" />
                      {t('my-docs:contextMenu.restore') || 'Restore'}
                    </DropdownMenuItem>
                  ) : isSigned ? (
                    <DropdownMenuItem onClick={() => setArchiveOpen(true)} className="cursor-pointer">
                      <Archive className="mr-2 h-4 w-4" />
                      {t('my-docs:contextMenu.archive') || 'Archive'}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-red-600 focus:text-red-600 cursor-pointer">
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('my-docs:contextMenu.delete')}
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }

      {/* Share Modal */}
      {
        canShare && (
          <SharingModal openShare={openShare} setOpenShare={setOpenShare} title={doc?.name} id={doc?.id} share={share} file={doc} />
        )
      }

      {/* Properties */}
      <PropertiesModal
        open={propsOpen}
        setOpen={setPropsOpen}
        resource={doc}
        type={isEimzoDocument ? 'document' : 'file'}
        onManageSharing={canShare ? () => setOpenShare(true) : undefined}
      />

      {/* Delete Modal */}
      <DeleteFile
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        file={doc}
        setData={onDelete}
        type={isEimzoDocument ? 'document' : 'file'}
      />

      {/* Visibility Change Dialog */}
      {
        canShare && (
          <Dialog open={visibilityOpen} onOpenChange={setVisibilityOpen}>
            <DialogContent className="bg-white text-black">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {t('my-docs:visibility.changeTitle')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <RadioGroup value={newVisibility} onValueChange={setNewVisibility} className="flex flex-col gap-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="private" id="vis-private" className="border-black" />
                    <Label htmlFor="vis-private" className="text-black flex items-center gap-2">
                      <Lock className="w-4 h-4" /> {t('my-docs:visibility.private')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="project" id="vis-project" className="border-black" />
                    <Label htmlFor="vis-project" className="text-black flex items-center gap-2">
                      <Building className="w-4 h-4" /> {t('my-docs:visibility.organization')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="public" id="vis-public" className="border-black" />
                    <Label htmlFor="vis-public" className="text-black flex items-center gap-2">
                      <Globe className="w-4 h-4" /> {t('my-docs:visibility.public')}
                    </Label>
                  </div>
                </RadioGroup>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setVisibilityOpen(false)}
                    className="text-black hover:bg-gray-100"
                  >
                    {t('my-docs:create.upload.btn.cancel')}
                  </Button>
                  <Button
                    onClick={handleVisibilityChange}
                    disabled={updateVisibility.loading}
                    className="text-white font-medium"
                    style={{
                      background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                    }}
                  >
                    {t('my-docs:visibility.save')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )
      }

      {/* Document Type Selector Modal - only for E-IMZO documents */}
      {
        canShare && isEimzoDocument && (
          <DocumentTypeSelector
            open={approvalStateOpen}
            onOpenChange={setApprovalStateOpen}
            fileId={doc.id}
            fileName={doc.name}
            currentType={approvalState || 'ordinary'}
            onSuccess={() => {
              if (onDelete) onDelete(); // Refresh the list
            }}
          />
        )
      }

      {/* Version History Modal */}
      {
        canShare && (
          <VersionHistoryModal
            open={versionHistoryOpen}
            onOpenChange={setVersionHistoryOpen}
            file={doc}
            setData={onDelete}
          />
        )
      }

      {/* Document Editor Modal */}
      <DocumentEditorModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        doc={doc}
        onSave={() => {
          if (onDelete) onDelete(); // Refresh the list
        }}
        collaborative={true}
      />

      {/* Activity Panel */}
      <ActivityPanel
        open={activityOpen}
        onOpenChange={setActivityOpen}
        fileId={doc.id}
      />

      {/* Comments Panel */}
      <CommentsPanel
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        fileId={doc.id}
      />

      {/* Clone Dialog */}
      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="bg-white text-black">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t('my-docs:clone.title') || 'Clone Document'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {isSigned && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                {t('my-docs:clone.signedWarning') || 'This document is signed. The clone will be created as an ordinary (editable) document without signatures.'}
              </div>
            )}
            <div>
              <Label htmlFor="clone-filename" className="text-sm font-medium">
                {t('my-docs:clone.newFilename') || 'New filename'}
              </Label>
              <Input
                id="clone-filename"
                value={cloneFilename}
                onChange={(e) => setCloneFilename(e.target.value)}
                placeholder={t('my-docs:clone.filenamePlaceholder') || 'Enter new filename'}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setCloneOpen(false)}
                className="text-black hover:bg-gray-100"
              >
                {t('common:cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleCloneFile}
                disabled={cloneFile.loading}
                className="text-white font-medium"
                style={{
                  background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                }}
              >
                {cloneFile.loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:cloning') || 'Cloning...'}
                  </>
                ) : (
                  t('my-docs:clone.confirm') || 'Clone'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ArchiveConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        onConfirm={handleConfirmArchive}
        loading={archiveFile.loading}
      />
      <RestoreConfirmDialog
        open={restoreOpen}
        onOpenChange={setRestoreOpen}
        onConfirm={handleConfirmRestore}
        loading={restoreFile.loading}
        needsFolderPick={restoreNeedsPick}
      />
    </>
  );
};
